import { useState, useEffect, useMemo, useCallback } from "react";
import { useInvitations, Invitation, InvitationsResponse } from "../../../hooks/useInvitations";
import { useAdminPlans } from "../../../hooks/useAdminUsage";
import { useTenants } from "../../../hooks/useTenants";
import { toast } from "sonner";
import i18n from "@/i18n";
import { Trash2, Mail, X, AlertCircle, Send } from "lucide-react";
import { PlanAssignmentModal } from "../../ui/PlanAssignmentModal";
import type { InvitationPayload } from "../../ui/planAssignmentModal.utils";
import {
  AccentBarHeading,
  IllustratedEmptyState,
  SummaryStatCard,
} from "@/components/ui/molecules";
import { DataTableOrganism } from "@/components/ui/organisms";
import { SummarizedListSectionTemplate } from "@/components/ui/templates";
import {
  createInvitationsTableColumns,
  mapInvitationToTableRow,
} from "./invitationsTable.columns";
import { Button } from "../../ui/atoms/Button";

interface InvitationsSectionProps {
  isAdmin: boolean;
  isLoading: boolean;
}

export const InvitationsSection = ({ isAdmin, isLoading }: InvitationsSectionProps) => {
  const [invitationToRevoke, setInvitationToRevoke] = useState<Invitation | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data: plans = [], error: plansError } = useAdminPlans({
    enabled: isAdmin && !isLoading,
  });

  const { data: tenantsData } = useTenants({
    enabled: isAdmin && !isLoading,
  });

  const {
    data: invitationsData,
    isLoading: isInvitationsInitialLoading,
    isFetching: isInvitationsFetching,
  } = useInvitations({ enabled: isAdmin && !isLoading });

  const { mutateAsync: createInvitationMutation, isPending: isSubmitting } =
    useInvitations().useCreateInvitation();
  const { mutateAsync: deleteInvitationMutation, isPending: isRevoking } =
    useInvitations().useDeleteInvitation();

  useEffect(() => {
    if (plansError) {
      toast.error(plansError instanceof Error && plansError.message !== i18n.t('common:toasts.admin.plans_load_failed', { lng: 'en' }) ? plansError.message : i18n.t('common:toasts.admin.plans_load_failed'));
    }
  }, [plansError]);

  const handleCreateInvitation = async (payload: InvitationPayload) => {
    if (!payload.email) {
      toast.error(i18n.t('common:toasts.email_required'));
      return;
    }

    try {
      await createInvitationMutation(payload);
      toast.success(i18n.t('settings:invite.success', { email: payload.email }));
      setIsCreateModalOpen(false);
    } catch (error) {
      toast.error(error instanceof Error && error.message.trim() ? error.message : i18n.t('settings:invite.error'));
      throw error;
    }
  };

  const handleRevoke = useCallback((invitation: Invitation) => {
    setInvitationToRevoke(invitation);
  }, []);

  const confirmRevoke = async () => {
    if (!invitationToRevoke) return;

    try {
      await deleteInvitationMutation(invitationToRevoke.id);
      toast.success(i18n.t('common:toasts.admin.invitation_revoked', { email: invitationToRevoke.email }));
      setInvitationToRevoke(null);
    } catch (error) {
      toast.error(error instanceof Error && error.message.trim() ? error.message : i18n.t('common:toasts.unexpected_error'));
    }
  };

  const cancelRevoke = () => {
    setInvitationToRevoke(null);
  };

  const invitations = useMemo(() => {
    if (!invitationsData) return [];
    const data = invitationsData as unknown as InvitationsResponse;
    return data?.invitations || [];
  }, [invitationsData]);

  const tableRows = useMemo(
    () => invitations.map((inv) => mapInvitationToTableRow(inv, plans)),
    [invitations, plans]
  );

  const invitationColumns = useMemo(
    () =>
      createInvitationsTableColumns({
        plans,
        onRevoke: handleRevoke,
      }),
    [plans, handleRevoke]
  );

  const emptyState = useMemo(() => {
    if (tableRows.length === 0) {
      return (
        <IllustratedEmptyState
          icon={<Mail />}
          title="No invitations found"
          description="Create an invitation to see it listed here."
        />
      );
    }
    return (
      <IllustratedEmptyState
        icon={<Mail />}
        title="No invitations found matching your search"
        description="Try another email, token fragment, plan name, or status keyword."
      />
    );
  }, [tableRows.length]);

  const heroSummary = (
    <SummaryStatCard
      label="Invitations Management"
      value="Create Invitations and Assign Plans"
      trailing={
        <Button
          type="contained"
          intent="primary"
          label="Create Invitation"
          onClick={() => setIsCreateModalOpen(true)}
          className="ml-4"
        />
      }
      icon={<Send className="text-purple" />}
    />
  );

  const refetchIndicator =
    isInvitationsFetching && !isInvitationsInitialLoading ? (
      <div className="flex items-center gap-2 text-sm text-dark-invirtus/60">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-purple border-t-transparent" />
        <span>Updating…</span>
      </div>
    ) : null;

  if (!isAdmin) {
    return null;
  }

  if (isInvitationsInitialLoading) {
    return (
      <SummarizedListSectionTemplate
        status="loading"
        loadingMessage="Loading invitations…"
      />
    );
  }

  return (
    <>
      <SummarizedListSectionTemplate
        status="ready"
        sectionGap="relaxed"
        summary={heroSummary}
        primary={
          <DataTableOrganism
            columns={invitationColumns}
            data={tableRows}
            pageSize={10}
            enableSelection
            enableSearch
            enableColumnVisibility
            columnVisibilityLabel="Column visibility"
            toolbarTitle={
              <AccentBarHeading>Existing Invitations</AccentBarHeading>
            }
            searchPlaceholder="Search by email, tenant, plan, status…"
            searchKeys={["email", "invitation_token", "tenantDisplay", "status", "planSearch"]}
            paginationItemLabel="invitations"
            toolbarEndSlot={refetchIndicator}
            dataTableProps={{
              emptyState,
            }}
          />
        }
      />

      <PlanAssignmentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        mode="create_invitation"
        plans={plans}
        tenants={tenantsData?.tenants}
        onSubmit={handleCreateInvitation}
        isSubmitting={isSubmitting}
      />

      {invitationToRevoke && (
        <div className="fixed inset-0 z-50 flex animate-in items-center justify-center bg-dark-invirtus/80 p-4 fade-in duration-300 backdrop-blur-sm">
          <div className="w-full max-w-md animate-in rounded-xl border border-light-lila/30 bg-white shadow-2xl slide-in-from-bottom duration-300">
            <div className="rounded-t-xl border-b border-light-lila/30 bg-gradient-to-r from-light-red/20 to-red/10 p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="rounded-lg bg-red/20 p-1.5">
                    <AlertCircle className="h-5 w-5 text-red" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-dark-invirtus">
                    Revoke Invitation
                  </h3>
                </div>
                <Button
                  type="icon"
                  intent="muted"
                  size="sm"
                  icon={<X/>}
                  onClick={cancelRevoke}
                  disabled={isRevoking}
                  label="Close"
                />
              </div>
            </div>

            <div className="p-4">
              <p className="text-sm font-medium leading-relaxed text-dark-invirtus/80">
                Are you sure you want to revoke the invitation for{" "}
                <span className="font-bold text-dark-invirtus">{invitationToRevoke.email}</span>?
              </p>
              <p className="mt-2 text-xs text-dark-invirtus/60">This action cannot be undone.</p>
            </div>

            <div className="flex justify-end gap-2 rounded-b-xl border-t border-light-lila/30 bg-light-lila/10 p-4">
              <Button
                id="cancel-revoke-btn"
                label="Cancel"
                type="outlined"
                intent="muted"
                size="sm"
                onClick={cancelRevoke}
                disabled={isRevoking}
              />
              <Button
                id="confirm-revoke-btn"
                label="Revoke Invitation"
                intent="danger"
                size="sm"
                icon={<Trash2/>}
                onClick={confirmRevoke}
                disabled={isRevoking}
                loading={isRevoking}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
